/*
* Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
*/

// Core imports
import { Fragment, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import '../../types';

// Component imports
import { Button, Col, Divider, Form, Input, Row, Typography } from 'antd';
import { MicrosoftIcon } from '../Icons/MicrosoftIcon';
import GoogleLoginButton from '../UI/GoogleLoginButton';
import TeamsMicrosoftLoginButton from '../TeamsMicrosoftLoginButton';
import ReactLoginMS from 'react-ms-login';
import OidcLoginButton from '../UI/OidcLoginButton';

// Misc imports
import { AUTHENTICATION_METHODS } from '../../constants/main';
import { MICROSOFT_CLIENT_ID } from '../../constants/env';
import { NAV_FORGOT_PASSWORD_REQUEST } from '../../constants/navigation';
import { useValidateUserInvitationMutation } from '../../store/users/usersApi';
import { useAuthenticateUserWithRedirects } from '../../utils/hooks/useAuthenticateUserWithRedirects';
import is from '../../utils/is';

/**
 * @param {Object} props
 * @param {OrganizationLoginSettings} props.loginSettings
 */
const LoginForm = ({ loginSettings, organizationId } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const inMicrosoftTeams = is.inMicrosoftTeams();
  const [form] = Form.useForm();
  const urlSearchParams = new URLSearchParams(location.search);
  const orgId = organizationId ?? urlSearchParams.get('orgId');

  const [loginUser, { isLoading: isLoggingInUser }] = useAuthenticateUserWithRedirects();
  const [, { data: validateUserInvitationData }] = useValidateUserInvitationMutation({ fixedCacheKey: 'validateUserInvitation' });

  useEffect(() => {
    if (validateUserInvitationData?.email) {
      form.setFieldsValue({
        email: validateUserInvitationData.email
      });
    }
  }, [validateUserInvitationData]);

  const onSubmit = async (authenticationMethod, values) => {
    let loginValues = {};

    switch (authenticationMethod) {
      case AUTHENTICATION_METHODS.GOOGLE:
        loginValues = { idToken: values.credential };
        break;
      case AUTHENTICATION_METHODS.MICROSOFT:
        loginValues = { accessToken: values.access_token };
        break;
      case AUTHENTICATION_METHODS.INTEGRATION:
        loginValues = { ...values };
        break;
      case AUTHENTICATION_METHODS.EMAIL:
      default:
        loginValues = { email: values.email, password: values.password };
        break;
    }

    if (values.organizationId) {
      loginValues = {
        ...loginValues,
        organizationId: values.organizationId
      };
    }

    loginUser({ authenticationMethod, data: loginValues, loginSettings });
  };

  const authPlugins = loginSettings?.plugins?.filter(plugin => plugin?.integration?.enabled) || [];
  const canLoginWithGoogle = !inMicrosoftTeams && (!orgId || loginSettings?.isGoogleLoginVisible);
  const canLoginWithMicrosoft = !orgId || loginSettings?.isMicrosoftLoginVisible;

  return (
    <Form
      name="loginForm"
      form={form}
      layout="vertical"
      onFinish={(values) => onSubmit(AUTHENTICATION_METHODS.EMAIL, values)}
      labelCol={{ xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 12 }}
      wrapperCol={{ xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 24 }}
      className="pt-10 pr-10 pl-10 pb-5 "
    >
      <Row className="text-center">
        <Col span={24}>
          <Typography.Title className="mb-2" level={2} strong>
            {t('Sign In')}
          </Typography.Title>
        </Col>
      </Row>

      <Row className="text-center mb-7">
        <Col span={24}>
          <Typography.Text className="fs-lg text-muted">
            {
              loginSettings?.isCustomMessageVisible
                ? loginSettings?.customMessage || ''
                : t('Please enter your details')
            }
          </Typography.Text>
        </Col>
      </Row>

      <Row>
        {
          orgId ? (
            <Form.Item
              name="organizationId"
              required={false}
              hidden={true}
              initialValue={orgId}
            >
              <Input name={'organizationId'} />
            </Form.Item>
          ) : null
        }

        {
          !orgId || loginSettings?.isCredentialLoginVisible ? (
            <Fragment>
              <Col span={24}>
                <Form.Item
                  label={t('Email')}
                  name="email"
                  required={false}
                  rules={[{
                    required: true,
                    type: 'email',
                    message: t('Please enter your email.')
                  }]}
                >
                  <Input
                    name={['email']}
                    placeholder={t('Enter your email')}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label={t('Password')}
                  required={false}
                  name="password"
                  className="mb-0"
                  rules={[{
                    required: true,
                    message: t('Please enter your password.')
                  }]}
                >
                  <Input.Password
                    placeholder={t('Enter your password')}
                    size="large"
                  />
                </Form.Item>

                <Button type="link" className="text-right pr-0 float-right mb-4" onClick={() => navigate(NAV_FORGOT_PASSWORD_REQUEST)} size="large">
                  {t('Forgot Password?')}
                </Button>
              </Col>

              <Col span={24}>
                <Form.Item className="mb-0">
                  <Button
                    className="mt-1"
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={isLoggingInUser}
                  >
                    {t('Sign In')}
                  </Button>
                </Form.Item>
              </Col>

              {
                canLoginWithGoogle || canLoginWithMicrosoft || authPlugins.length > 0 ? (
                  <Col xs={24}>
                    <Divider>{t('or')}</Divider>
                  </Col>
                ) : null
              }
            </Fragment>
          ) : null
        }

        {
          canLoginWithGoogle ? (
            <Col span={24}>
              <GoogleLoginButton
                onSuccess={(response) => onSubmit(AUTHENTICATION_METHODS.GOOGLE, { ...response, organizationId: orgId })}
                buttonContent={t('Sign In with Google')}
              />
            </Col>
          ) : null
        }

        {
          canLoginWithMicrosoft ? (
            <Col span={24}>
              {
                /*
                * NOTE: when Teams is running through a browser window
                * instead of the desktop client, authorization should
                * be handled the same way we do for the default web app
                *
                * The way to tell them apart is by checking the user agent
                */
                window.navigator.userAgent.includes('Teams/') || window.gc ? (
                  <TeamsMicrosoftLoginButton
                    handleLogin={(response) => onSubmit(AUTHENTICATION_METHODS.MICROSOFT, { ...response, organizationId: orgId })}
                    clientId={MICROSOFT_CLIENT_ID}
                    redirectUri={`${window.location.origin.toString()}/ms-auth.html`}
                    responseType="token"
                    cssClass="ant-btn ant-btn-default ant-btn-lg ant-btn-block"
                    btnContent={
                      <Fragment>
                        <MicrosoftIcon className="mr-3" />
                        {t('Sign In with Microsoft')}
                      </Fragment>
                    }
                  />
                ) : (
                  <ReactLoginMS
                    handleLogin={(response) => onSubmit(AUTHENTICATION_METHODS.MICROSOFT, { ...response, organizationId: orgId })}
                    clientId={MICROSOFT_CLIENT_ID}
                    redirectUri={`${window.location.origin.toString()}/ms-auth.html`}
                    responseType="token"
                    cssClass="ant-btn ant-btn-default ant-btn-lg ant-btn-block mb-3"
                    btnContent={
                      <Fragment>
                        <MicrosoftIcon className="mr-3" />
                        {t('Sign In with Microsoft')}
                      </Fragment>
                    }
                  />
                )
              }
            </Col>
          ) : null
        }

        {
          authPlugins.map((plugin, index) => {
            return (
              <Col span={24} key={plugin.integration.id}>
                <OidcLoginButton
                  integration={plugin.integration}
                  organizationId={orgId}
                  onSuccess={(response) => onSubmit(AUTHENTICATION_METHODS.INTEGRATION, response)}
                  buttonContent={t('Sign In with {{integrationName}}', { integrationName: plugin.integration.name })}
                />
              </Col>
            );
          })
        }
      </Row>
    </Form>
  );
};

LoginForm.propTypes = {
  loginSettings: PropTypes.object,
  /**
   * Override the organization ID to be a specific value.
   */
  organizationId: PropTypes.string
};

export default LoginForm;
